/* eslint-disable css-modules/no-unused-class  */
import React, { FC, RefObject } from 'react';
import { Divider, SmallLoader } from 'learning-panel-components';
import { useSelector } from 'react-redux';
import { selectTenantName, selectUserUuid } from '@redux/slices/userSlice';
import sha1 from 'crypto-js/sha1';
import styles from './certificate.module.scss';

const Certificate: FC<CertificateProps> = (props: CertificateProps) => {
    const {
        certificateRef,
        refCert,
        certificateData,
        isCxUniversity,
        isLearningPath,
        isSkillTree,
        isCustom,
        isOwasp,
        isBP,
        isCertificateDataLoading,
        width,
        height,
        withOpacity
    } = props;
    const { name, entityDisplayName, endDate, completedAt } = certificateData;
    const tenant = useSelector(selectTenantName);
    const userUuid = useSelector(selectUserUuid);
    // Use UUID instead of user ID for consistency across Rails and Node.js databases
    const hash = sha1(`${tenant}${userUuid}`).toString();

    const getBackgroundImage = () => {
        if (isBP && isSkillTree) return `${process.env.PUBLIC_URL}/images/certificateSPbp.png`;
        if (isBP && isLearningPath) return `${process.env.PUBLIC_URL}/images/bpCertificateLP.png`;
        if (isSkillTree) return `${process.env.PUBLIC_URL}/images/security_champion_certificatev2.png`;
        if (isLearningPath) return `${process.env.PUBLIC_URL}/images/learningPathCertificate.png`;
        if (isCxUniversity && isCustom) return `${process.env.PUBLIC_URL}/images/cxUni_certificate_background.png`;
        if (isCustom) return `${process.env.PUBLIC_URL}/images/customCertificate.png`;
        if (isOwasp) return `${process.env.PUBLIC_URL}/images/owaspCertificate.png`;

        return `${process.env.PUBLIC_URL}/images/background_certificate_2.0.jpg`;
    };

    const renderCourseDescription = () => {
        if (isLearningPath) return <div className={styles.text2}>Learning Path</div>;
        if (isSkillTree) return <div className={styles.text2}>Checkmarx program</div>;
        if (isCxUniversity && isCustom) return <div className={styles.text2}>Checkmarx University training</div>;
        if (isOwasp) return <div className={styles.text2}>Checkmarx Codebashing course</div>;
        if (isCustom) return <></>;
        return <div className={styles.text2}>Checkmarx Codebashing course</div>;
    };

    const renderCompleted = () => {
        if (isCustom) return <div className={styles.text1}>has successfully completed the course</div>;
        return <div className={styles.text1}>has successfully completed the </div>;
    };

    const renderLink = () => {
        if (isCxUniversity && isCustom) return 'cxuniversity.codebashing.com';
        return 'www.codebashing.com';
    };

    return (
        <div
            ref={refCert}
            className={styles['certificate-wrapper']}
            style={{ position: 'relative', width, height, opacity: withOpacity ? 0.4 : 1 }}
        >
            <img alt="background_certificate" className={styles['background-image']} src={getBackgroundImage()} />
            <div className={styles['certificate-data-wrapper']}>
                {isCertificateDataLoading || !certificateData.name ? (
                    <SmallLoader size="85px" />
                ) : (
                    <div ref={certificateRef} className={styles['certificate-data']}>
                        <div className={styles.title}>CERTIFICATE OF COMPLETION</div>
                        <div className={styles.subtitle}>To certify that</div>
                        <div className={styles.name}>{name}</div>
                        <div className={styles.divider}>
                            <Divider color="rgba(255, 255, 255, 0.25)" />
                        </div>
                        {renderCompleted()}
                        {renderCourseDescription()}
                        <div className={styles.course}>{entityDisplayName}</div>
                        <div className={styles.link}>{renderLink()}</div>
                        <div className={styles['bottom-line']}>
                            <div className={styles.left}>
                                <div className={styles.id}>Authenticity ID </div>
                                <div className={styles['id-value']}>{hash}</div>
                            </div>
                            <div className={styles.right}>
                                <div className={styles.valid}>{completedAt ? 'Completed At' : 'Valid Until'}</div>
                                <div className={styles['valid-value']}>{completedAt || endDate}</div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
interface CertificateProps {
    certificateRef?: React.RefObject<HTMLDivElement>;
    certificateData: {
        name: string;
        entityDisplayName: string;
        endDate?: string;
        completedAt?: string;
        icon?: string;
    };
    isCxUniversity: boolean;
    isCustom: boolean;
    isOwasp: boolean;
    isSkillTree: boolean;
    isLearningPath: boolean;
    isBP?: boolean;
    refCert?: RefObject<HTMLDivElement>;
    isCertificateDataLoading: boolean;
    width?: string;
    height?: string;
    withOpacity?: boolean;
}
export default Certificate;
